'use client';

import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { CLUBS_PLACEHOLDER_IMAGE } from '@pickleballinc/ui/components/global/utils/constants';
import { useMemo, useState } from 'react';

import { ModuleCard } from './ModuleCard';
import { ModuleCardBody } from './ModuleCardBody';

export type UseFavoriteState = (
	id: string,
	initialIsFavorite?: boolean
) => {
	isFavorite: boolean;
	isFavoriteDisabled: boolean;
	isUserLoggedIn: boolean;
	onToggle: (event: React.MouseEvent, id?: string, isFavourite?: boolean | null) => void;
};

const useDefaultFavoriteState: UseFavoriteState = () => ({
	isFavorite: false,
	isFavoriteDisabled: true,
	isUserLoggedIn: false,
	onToggle: () => {}
});

export interface ClubCardProps {
	id: string;
	slug: string;
	title: string;
	location: string;
	logo: string;
	memberCount: number;
	expired?: boolean | null;
	clubsAppBaseURL: string;
	customCardHref?: string;
	hideCardHref?: boolean;
	openInNewTab?: boolean;
	/**
	 * Render the click-through as a plain `<a>` (full document navigation) instead of a Next `<Link>`.
	 * Set by the listing-ad rotator: an ad's URL carries Everflow parameters that the landing middleware
	 * has to see, and Next would route a same-origin URL client-side and skip it.
	 */
	nativeAnchor?: boolean;
	linkRel?: string;
	className?: string;
	logoClassName?: string;
	showFavoriteIcon?: boolean;
	useFavoriteState?: UseFavoriteState;
	initialIsFavorite?: boolean;
	variant?: 'light' | 'dark';
}

export const ClubCard = (props: ClubCardProps) => {
	const { id, slug, customCardHref, clubsAppBaseURL, hideCardHref = false } = props;

	const href = hideCardHref ? undefined : customCardHref ? customCardHref : `${clubsAppBaseURL}/clubs/${slug ? slug : id}`;

	return <ClubCardComponent {...props} href={href} />;
};

const ClubCardComponent = ({
	id,
	title,
	location,
	logo,
	memberCount,
	expired,
	className,
	logoClassName,
	href,
	openInNewTab,
	nativeAnchor,
	linkRel,
	showFavoriteIcon,
	variant = 'light',
	useFavoriteState,
	initialIsFavorite
}: ClubCardProps & { href?: string }) => {
	const [isPlaceholderImage, setIsPlaceholderImage] = useState(false);

	const getImageToDisplay = useMemo(() => {
		if (logo) {
			if (logo === CLUBS_PLACEHOLDER_IMAGE) setIsPlaceholderImage(true);
			return getCDNURL(logo, 566, 384);
		}
		setIsPlaceholderImage(true);
		return getCDNURL(CLUBS_PLACEHOLDER_IMAGE, 566, 384);
	}, [logo]);

	const images = [getImageToDisplay];

	const membersLabel = `${memberCount || 0} member${memberCount != 1 ? 's' : ''}`;

	const { isFavorite, isFavoriteDisabled, isUserLoggedIn, onToggle } = (useFavoriteState ?? useDefaultFavoriteState)(id, initialIsFavorite);

	return (
		<div className="flex flex-col h-full gap-2">
			<div className="relative min-[500px]:min-w-[224px] min-[500px]:max-w-[286px]">
				<ModuleCard
					id={id}
					images={images}
					imageAlt={title}
					isPlaceholderImage={isPlaceholderImage}
					href={href}
					target={openInNewTab ? '_blank' : '_self'}
					rel={linkRel}
					nativeAnchor={nativeAnchor}
					className={className}
					imageContainerClassName={logoClassName}
					imageClassName={logoClassName}
					showFavoriteIcon={showFavoriteIcon}
					isUserLoggedIn={isUserLoggedIn}
					isFavourite={isFavorite}
					isFavoriteDisabled={isFavoriteDisabled}
					onFavoriteToggle={onToggle}
					topLeftSlot={
						expired ? (
							<div className="absolute top-0 left-0 right-0 z-10 !bg-error-600 text-white text-[10px] sm:text-xs font-semibold text-center py-0.5 sm:py-1 px-1 sm:px-2">
								Expired Membership
							</div>
						) : null
					}
				>
					<ModuleCardBody
						title={title}
						subtitle={location}
						metaLines={[{ id: 'members-label', content: membersLabel }]}
						footerRight={null}
						variant={variant}
					/>
				</ModuleCard>
			</div>
		</div>
	);
};
